import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, BookOpen, Sparkles, Compass, Clock, Share2, Plus, Users } from "lucide-react";
import { AnalysisPanel } from "./AnalysisPanel";
import { ConceptMap } from "./ConceptMap";
import { FollowUpChat } from "./FollowUpChat";
import { MediaGallery } from "./MediaGallery";
import { TranslateControl } from "./TranslateControl";
import { SubmitReferenceModal } from "./SubmitReferenceModal";
import { UnifiedCognitivePanel } from "./UnifiedCognitivePanel";
import { ExportControl } from "./ExportControl";
import { exportDocument } from "@/lib/exportDocument";
import { getApprovedReferences } from "@/lib/rexxponsApi";

const MODES = [
  { id: "read", label: "Read", icon: BookOpen },
  { id: "interpret", label: "Interpret", icon: Sparkles },
  { id: "explore", label: "Explore", icon: Compass },
];

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function buildEntityRegex(entities) {
  const cleaned = [...new Set(entities.filter((e) => e && e.length > 2))].sort((a, b) => b.length - a.length);
  if (cleaned.length === 0) return null;
  return new RegExp(`(${cleaned.map(escapeRe).join("|")})`, "gi");
}

const Chip = ({ text, onClick, active }) => (
  <button
    data-testid="entity-chip"
    onClick={onClick}
    className={`inline rounded px-1 -mx-0.5 border transition-colors ${
      active
        ? "border-primary bg-primary/25 text-primary"
        : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
    }`}
  >
    {text}
  </button>
);

function HighlightedParagraph({ text, regex, onEntity, activeName }) {
  if (!regex) return <p>{text}</p>;
  const parts = text.split(regex);
  return (
    <p>
      {parts.map((part, i) => {
        if (i % 2 === 1) {
          const active = activeName && part.toLowerCase() === activeName.toLowerCase();
          return <Chip key={`${i}-${part}`} text={part} active={active} onClick={() => onEntity(part)} />;
        }
        return <React.Fragment key={`${i}-${part.slice(0, 12)}`}>{part}</React.Fragment>;
      })}
    </p>
  );
}

const ModeToggle = ({ mode, setMode }) => (
  <div className="flex items-center bg-secondary rounded-full p-1 border border-border">
    {MODES.map((m) => {
      const active = mode === m.id;
      const Icon = m.icon;
      return (
        <button
          key={m.id}
          data-testid={`mode-${m.id}`}
          onClick={() => setMode(m.id)}
          className={`flex items-center gap-1.5 rounded-full px-3 sm:px-3.5 py-1.5 text-sm font-medium transition-colors ${
            active ? "bg-primary/15 text-primary nexx-glow" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon strokeWidth={1.5} className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{m.label}</span>
        </button>
      );
    })}
  </div>
);

const CommunityRefs = ({ refs }) => {
  if (!refs || refs.length === 0) return null;
  return (
    <div className="mt-16 border-t border-border pt-10" data-testid="reader-community-refs">
      <div className="flex items-center gap-2.5 mb-4">
        <Users strokeWidth={1.5} className="w-4 h-4 text-primary" />
        <span className="font-sans text-xs tracking-[0.18em] uppercase text-primary">Community References</span>
      </div>
      <ul className="divide-y divide-border border-t border-b border-border">
        {refs.map((r, i) => (
          <li key={`${i}-${r.url}`} data-testid="reader-community-ref-item">
            <a href={r.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 py-4 hover:bg-secondary/40 -mx-2 px-2 transition-colors">
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{r.title}</p>
                <p className="text-xs text-muted-foreground truncate">{r.note || r.url}</p>
              </div>
              <ExternalLink strokeWidth={1.5} className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const CognitiveReader = ({ doc, onBack, onShare }) => {
  const [current, setCurrent] = useState(doc);
  const [mode, setMode] = useState("interpret");
  const [selected, setSelected] = useState(null);
  const [approved, setApproved] = useState([]);
  const [refOpen, setRefOpen] = useState(false);

  useEffect(() => { setCurrent(doc); setSelected(null); }, [doc]);
  useEffect(() => { getApprovedReferences(doc.id).then(setApproved); }, [doc.id]);

  const regex = useMemo(() => buildEntityRegex(current.key_entities || []), [current.key_entities]);

  const conceptByEntity = (entity) => {
    const e = entity.toLowerCase();
    return (
      current.concepts.find((c) => c.name.toLowerCase() === e) ||
      current.concepts.find((c) => e.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(e)) ||
      { name: entity, summary: "A key entity highlighted in this article.", related: [] }
    );
  };

  const onEntity = (entity) => setSelected(conceptByEntity(entity));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="relative z-10 min-h-screen w-full pt-16"
    >
      {/* Reader sub-bar */}
      <div className="sticky top-16 z-30 nexx-glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <button
            data-testid="reader-back-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <ModeToggle mode={mode} setMode={setMode} />
            <div className="hidden sm:block"><TranslateControl docId={doc.id} docKind="interpret" original={doc} onTranslated={setCurrent} /></div>
            <button
              data-testid="reader-add-source-btn"
              onClick={() => setRefOpen(true)}
              aria-label="Contribute a source"
              className="flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            >
              <Plus strokeWidth={1.75} className="w-4 h-4" />
            </button>
            <ExportControl
              testid="reader-export-btn"
              onExport={(lang) => exportDocument(doc, "reader", { lang })}
              hasUnified={!!doc.unified_output}
            />
            <button
              data-testid="reader-share-btn"
              onClick={() => onShare?.(`${current.title}\n\n${current.insight_summary}\n\nInterpreted with Rexxpons — ${current.url}`)}
              aria-label="Share insight"
              className="flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            >
              <Share2 strokeWidth={1.5} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Article header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4 flex-wrap">
            {current.site_name && <span className="text-primary/90">{current.site_name}</span>}
            <span className="flex items-center gap-1">
              <Clock strokeWidth={1.5} className="w-3.5 h-3.5" /> {current.read_time_min} min read
            </span>
            <a href={current.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-foreground transition-colors">
              <ExternalLink strokeWidth={1.5} className="w-3.5 h-3.5" /> Original
            </a>
          </div>
          <h1 data-testid="reader-title" className="font-heading font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-foreground">
            {current.title}
          </h1>
        </div>

        {/* READ */}
        {mode === "read" && (
          <div data-testid="read-view" className="mx-auto max-w-[70ch] break-words bg-[#F6F8FA] text-slate-800 rounded-2xl p-8 sm:p-12 reading-col text-lg leading-relaxed">
            {current.paragraphs.map((p, i) => (
              <p key={`${i}-${p.slice(0, 16)}`}>{p}</p>
            ))}
          </div>
        )}

        {/* INTERPRET */}
        {mode === "interpret" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <article data-testid="interpret-view" className="lg:col-span-7 min-w-0 break-words reading-col text-lg leading-relaxed text-foreground/90">
              {current.paragraphs.map((p, i) => (
                <HighlightedParagraph
                  key={`${i}-${p.slice(0, 16)}`}
                  text={p}
                  regex={regex}
                  onEntity={onEntity}
                  activeName={selected?.name}
                />
              ))}
            </article>
            <aside className="lg:col-span-5 min-w-0">
              <div className="lg:sticky lg:top-32 rounded-2xl border border-border nexx-glass p-6 max-h-[calc(100vh-10rem)] overflow-y-auto scrollbar-thin">
                <AnalysisPanel doc={current} selected={selected} onSelect={setSelected} />
              </div>
            </aside>
          </div>
        )}

        {/* EXPLORE */}
        {mode === "explore" && (
          <div data-testid="explore-view" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 min-w-0 rounded-2xl border border-border bg-secondary/30 h-[60vh] lg:h-[70vh] p-4">
              <ConceptMap
                concepts={current.concepts}
                title={current.site_name || "Topic"}
                selectedName={selected?.name}
                onSelect={setSelected}
                variant="full"
              />
            </div>
            <div className="lg:col-span-4 min-w-0">
              <div className="lg:sticky lg:top-32 rounded-2xl border border-border nexx-glass p-6">
                <p className="text-[11px] tracking-[0.28em] uppercase text-muted-foreground mb-4">Concept Detail</p>
                {selected ? (
                  <div>
                    <h3 className="font-heading text-xl text-primary mb-2">{selected.name}</h3>
                    <p className="text-sm text-foreground/80 leading-relaxed mb-5">{selected.summary}</p>
                    {selected.related?.length > 0 && (
                      <>
                        <p className="text-xs tracking-[0.14em] uppercase text-muted-foreground mb-2">Connected concepts</p>
                        <div className="flex flex-wrap gap-2">
                          {selected.related.map((r) => {
                            const t = current.concepts.find((c) => c.name.toLowerCase() === r.toLowerCase());
                            return (
                              <button
                                key={r}
                                onClick={() => t && setSelected(t)}
                                className="text-xs rounded-full px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                              >
                                {r}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Select a concept node to explore its meaning and connections.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {doc.unified_output && <UnifiedCognitivePanel unified={doc.unified_output} />}

        <MediaGallery query={doc.title} />

        <CommunityRefs refs={approved} />

        <FollowUpChat docId={current.id} docKind="interpret" />
      </div>

      <SubmitReferenceModal open={refOpen} onClose={() => setRefOpen(false)} docId={doc.id} docKind="interpret" />
    </motion.div>
  );
};
